import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useOrcamento } from '../context/OrcamentoContext';
import { apiFetch } from '../lib/api';
import LogService, { LOG_ACTIONS } from '../services/LogService';

const PROGRESS_STEPS = [
  'Lendo planta...',
  'Identificando comodos...',
  'Calculando areas...',
  'Detectando itens especiais...',
  'Finalizando analise...',
];

function OrcamentoAnalise() {
  const navigate = useNavigate();
  const { file, filePreview, tipo, padrao, estado, setAnalise } = useOrcamento();
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState('');
  const [, setLoading] = useState(true);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!file) {
      navigate('/orcamento', { replace: true });
      return;
    }

    if (hasStarted.current) return;
    hasStarted.current = true;

    const intervals = [];
    let step = 0;
    const advanceStep = () => {
      step++;
      if (step < PROGRESS_STEPS.length - 1) {
        setCurrentStep(step);
      }
    };
    for (let i = 1; i < PROGRESS_STEPS.length - 1; i++) {
      const delay = 2000 + Math.random() * 4000;
      const totalDelay = i * 3000 + delay - 3000;
      intervals.push(setTimeout(advanceStep, totalDelay));
    }

    const callApi = async () => {
      try {
        LogService.log(LOG_ACTIONS.ORCAMENTO_ANALISE, {
          details: { tipo, padrao, estado },
        });

        const response = await apiFetch('/api/orcamento/analisar', {
          method: 'POST',
          body: {
            file: file.base64,
            mimeType: file.type,
            tipo,
            padrao,
          },
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `Erro ${response.status}`);
        }

        const data = await response.json();
        setCurrentStep(PROGRESS_STEPS.length - 1);

        setTimeout(() => {
          setAnalise(data);
          setLoading(false);
          navigate('/orcamento/validacao', { replace: true });
        }, 800);
      } catch (err) {
        console.error('Analysis error:', err);
        setError(err.message || 'Erro ao analisar planta. Tente novamente.');
        setLoading(false);
        intervals.forEach(clearTimeout);
      }
    };

    callApi();

    return () => {
      intervals.forEach(clearTimeout);
    };
  }, [file, tipo, padrao, estado, navigate, setAnalise]);

  const handleRetry = () => {
    setError('');
    setLoading(true);
    setCurrentStep(0);
    hasStarted.current = false;
  };

  if (!file) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-10 md:py-16">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-10">
          {/* Bruno with magnifying glass */}
          <div className="flex justify-center mb-6">
            <img
              src="/images/brand/bruno-lupa.png"
              alt="Bruno analisando"
              className="w-28 h-28 md:w-36 md:h-36 object-contain"
            />
          </div>

          {/* Small preview */}
          {filePreview && (
            <div className="flex justify-center mb-6">
              <img src={filePreview} alt="Planta" className="w-20 h-20 object-cover rounded-lg border border-gray-200 opacity-60" />
            </div>
          )}

          {error ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-2 font-heading">Erro na Analise</h2>
              <p className="text-sm text-gray-500 mb-6">{error}</p>
              <button
                onClick={handleRetry}
                className="px-6 py-3 text-sm font-bold text-white bg-brand-orange rounded-xl hover:bg-brand-orange-dark cursor-pointer transition-colors"
              >
                Tentar Novamente
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-navy text-center mb-2 font-heading">Analisando sua planta...</h2>
              <p className="text-sm text-gray-400 text-center mb-8">O Bruno esta identificando comodos, areas e itens da sua obra</p>

              {/* Progress Steps */}
              <div className="space-y-3 max-w-sm mx-auto">
                {PROGRESS_STEPS.map((label, i) => (
                  <div key={i} className="flex items-center gap-3">
                    {i < currentStep ? (
                      <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    ) : i === currentStep ? (
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                        <div className="w-5 h-5 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : (
                      <div className="w-7 h-7 border-2 border-gray-200 rounded-full flex-shrink-0" />
                    )}
                    <span className={`text-sm ${
                      i < currentStep ? 'text-green-700 font-medium' :
                      i === currentStep ? 'text-brand-blue font-medium' :
                      'text-gray-400'
                    }`}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Progress bar */}
              <div className="mt-8 bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-brand-orange h-full rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${((currentStep + 1) / PROGRESS_STEPS.length) * 100}%` }}
                />
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default OrcamentoAnalise;
